import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { portalGroupAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const EMPLOYMENT_STATUS_LABELS = {
  active: 'Active employment',
  inactive: 'Inactive employment',
  probation: 'On probation',
  notice: 'Serving notice',
};

const AdminUsersCard = () => {
  const { user } = useAuth();
  const canView = Boolean(user?.has_admin_access || user?.is_superuser);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const response = await portalGroupAPI.getAdminUsers();
      return response.data;
    },
    enabled: canView,
    staleTime: 60 * 1000,
    retry: false,
  });

  if (!canView) return null;

  const users = data?.users || [];
  const summary = data?.summary || {};

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow col-span-full">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900">Admin Users</h3>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex-shrink-0">
            {isLoading ? '…' : `${summary.total || 0} ${summary.total === 1 ? 'account' : 'accounts'}`}
          </span>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          These people can open the Admin Portal because Admin Access or Superuser is turned on and login is still enabled. System accounts are not listed. This list is separate from the HR group below.
        </p>
      </div>

      {isLoading ? (
        <div className="px-6 py-8 flex items-center justify-center gap-2 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading admin users...</span>
        </div>
      ) : isError ? (
        <div className="px-6 py-8 text-sm text-gray-500">
          The admin-user list could not be loaded.
        </div>
      ) : (
        <div className="px-6 py-4">
          <div className="flex flex-wrap gap-2 mb-4">
            <SummaryPill label="Admin Access" count={summary.admin_access || 0} tone="red" />
            <SummaryPill label="Superuser" count={summary.superuser || 0} tone="purple" />
            <SummaryPill label="Worker Admin" count={summary.worker_admin || 0} tone="amber" />
            <SummaryPill label="Login enabled" count={summary.login_enabled || 0} tone="green" />
          </div>

          {users.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">No admin accounts are configured.</p>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[32rem] overflow-y-auto">
              {users.map((account) => (
                <AdminUserRow key={account.staff_id} account={account} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SummaryPill = ({ label, count, tone }) => {
  const tones = {
    red: 'bg-red-50 text-red-800',
    purple: 'bg-purple-50 text-purple-800',
    amber: 'bg-amber-50 text-amber-800',
    green: 'bg-green-50 text-green-800',
  };
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${tones[tone]}`}>
      <span>{label}</span>
      <span className="font-semibold">{count}</span>
    </span>
  );
};

const AdminUserRow = ({ account }) => {
  const initials = `${account.first_name?.[0] || ''}${account.last_name?.[0] || ''}`.toUpperCase() || account.staff_id.slice(0, 2);
  const employmentLabel = EMPLOYMENT_STATUS_LABELS[account.employment_status] || account.employment_status;
  const detail = [account.designation, account.department, account.employment_classification_display]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-gray-900 truncate">
              {account.full_name || account.staff_id}
            </span>
            <span className="text-xs text-gray-400 flex-shrink-0">{account.staff_id}</span>
          </div>
          {detail && <p className="text-xs text-gray-500 truncate">{detail}</p>}
          <p className="text-xs text-gray-400">
            {account.login_enabled ? 'Login enabled' : 'Login disabled'}
            {employmentLabel ? ` · ${employmentLabel}` : ''}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 sm:justify-end sm:max-w-xs">
        {account.has_admin_access && <AccessBadge label="Admin Access" tone="red" />}
        {account.is_superuser && <AccessBadge label="Superuser" tone="purple" />}
        {account.has_worker_admin_access && <AccessBadge label="Worker Admin" tone="amber" />}
      </div>
    </div>
  );
};

const AccessBadge = ({ label, tone }) => {
  const tones = {
    red: 'bg-red-100 text-red-800 border-red-200',
    purple: 'bg-purple-100 text-purple-800 border-purple-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${tones[tone]}`}>
      {label}
    </span>
  );
};

export default AdminUsersCard;
